import type { GameMode } from '@sstk/map-schema';

/**
 * A floor level index. Any integer is valid - games may use 0 for ground,
 * positive values for upper floors, negative for sub-levels, etc.
 *
 * Previously typed as `-1 | 0 | 1`; widened to plain `number` so games are
 * not artificially constrained to three floors.
 */
export type MapFloorLevel = number;

export type MapAreaKind = 'room' | 'corridor' | 'lobby' | 'connector';

/**
 * @deprecated Use `MapTriggerVolume` with a registry-registered handler kind
 * (`'radio'`, `'door'`, `'jumpscare'`) instead. This type will be removed in a
 * future schema version.
 */
export type InteractiveObjectType = 'radio' | 'door' | 'jumpscare-prop';

/**
 * The texture families the runtime can generate procedurally. Each is drawn
 * into a canvas at load time (see `runtime/surfaces.ts`), so a surface finish
 * costs no network request and no asset pipeline - `'none'` is a flat colour.
 */
export type MapSurfaceTexture =
  | 'none'
  | 'plaster'
  | 'wallpaper'
  | 'panelling'
  | 'brick'
  | 'concrete'
  | 'tile'
  | 'floorboard'
  | 'carpet'
  | 'lino'
  | 'stone'
  | 'metal';

/**
 * A surface finish for a floor panel, a wall, or a ceiling. Every field is
 * optional so a document authored before surfaces existed still renders: the
 * panel/wall `color` remains the fallback tint and `'none'` the fallback
 * texture, which reproduces the previous flat-colour look exactly.
 */
export interface MapSurface {
  /** Procedural texture family. Defaults to `'none'` (flat colour). */
  texture?: MapSurfaceTexture;
  /** Base tint multiplied over the texture. Defaults to the owner's `color`. */
  color?: string;
  /** Texture repeats per metre. Defaults to 1. */
  scale?: number;
  roughness?: number;
  metalness?: number;
  /** Emissive strength 0..1, emitting `color`. Defaults to 0. */
  emissive?: number;
}

/**
 * Which side of a wall its generated trim stands proud of, named as a signed
 * axis in the wall's own local frame (before `rotationY`).
 *
 * `'both'` is how trim has always been drawn: one box the length of the wall,
 * standing `TRIM_PROJECTION` proud of it on each side. That is right for a
 * partition with a room either side of it, and wrong for an exterior wall,
 * where the outward half of every skirting, dado and cornice is buried in
 * whatever the outside skin is - and pokes through it wherever that skin
 * stands off the wall by less than the projection. Measured in The Dwindling:
 * a 30 mm projection against a 140 mm brick skin whose inner face is flush
 * with the wall, so every run of painted joinery on an outside wall came
 * through the brickwork.
 *
 * The axis is named rather than assumed because a wall may be authored thin
 * on either horizontal axis: use `'+x'`/`'-x'` when the wall's thickness is
 * `size[0]`, `'+z'`/`'-z'` when it is `size[2]`, and give the side the room
 * is on. The trim then projects on that side only and sits flush on the
 * other, and runs the wall's own length with no overhang at its ends.
 */
export type MapWallTrimFaces = 'both' | '+x' | '-x' | '+z' | '-z';

/**
 * The extruded cross-section a trim member is cut to. Absent means the
 * plain rectangular box trim has always been - a skirting, dado or cornice
 * is flat on every face, so at a grazing angle (looking near-parallel down
 * a long run) it is a stack of parallel, near-coplanar faces a pixel or two
 * apart, which antialiasing softens but does not stop crawling as the
 * camera moves. A real moulding profile has no two adjacent faces close to
 * parallel, so there is nothing left for that to happen to.
 *
 * `'ovolo'` is the one shape here: a flat foot at the member's own base,
 * standing the full projection proud of the wall, eased back flush with it
 * by a quarter-round at the top - see `wallTrimProfilePoints`. One shape
 * rather than a set of period styles because nothing in any consuming
 * package's map document asks for more than "a moulding, not a box" yet;
 * the type is a string union rather than a bare boolean so a second style
 * is an additive change to it, not a breaking one.
 */
export type MapWallTrimProfile = 'ovolo';

/** Wall trim the renderer generates from the wall's own extents. */
export interface MapWallTrim {
  /** Skirting board along the wall base. */
  skirting?: boolean;
  /** Cornice along the wall head. */
  cornice?: boolean;
  /** Dado/picture rail at this height (metres from the floor) when set. */
  dadoHeight?: number;
  /** Trim colour. Defaults to a darker shade of the wall surface colour. */
  color?: string;
  /**
   * Which face(s) the trim stands proud of. Absent means `'both'`, so a
   * document written before this existed draws exactly as it always did.
   */
  faces?: MapWallTrimFaces;
  /**
   * The member's cross-section. Absent draws the plain box every trim has
   * always been drawn as, byte for byte - see `MapWallTrimProfile`.
   */
  profile?: MapWallTrimProfile;
}

export interface MapFloorPanel {
  id: string;
  center: [number, number];
  size: [number, number];
  color: string;
  ceilingHeight: number;
  /** Floor finish. Falls back to a flat `color` surface. */
  surface?: MapSurface;
  /**
   * Ceiling finish. When present the renderer caps the panel at
   * `ceilingHeight`; when absent no ceiling is drawn (the previous behaviour).
   */
  ceiling?: MapSurface;
}

export interface MapWallSegment {
  id: string;
  position: [number, number, number];
  size: [number, number, number];
  rotationY?: number;
  color?: string;
  /** Wall finish. Falls back to a flat `color` surface. */
  surface?: MapSurface;
  /** Generated skirting/cornice/dado. Absent means an untrimmed slab. */
  trim?: MapWallTrim;
}

export interface MapBlocker {
  id: string;
  center: [number, number];
  size: [number, number];
  height: number;
  color?: string;
}

export interface MapAreaMeta {
  id: string;
  name: string;
  floor: MapFloorLevel;
  kind: MapAreaKind;
}

/**
 * A room, authored as a rectangle rather than as individually placed walls.
 *
 * Rooms are the source of truth the room-authoring tool derives `walls`/
 * `panels`/`areas` from (see `deriveRoomGeometry` in
 * `engine/editor/roomDerivation.ts`): rooms that share an edge share one
 * wall run instead of two coincident boxes, and a doorway is a gap cut from
 * that run rather than a separate prop standing in front of solid geometry.
 * Purely authoring metadata - the runtime only ever reads the derived
 * `walls`/`panels`/`areas`, exactly as before this existed; an older
 * document with no `rooms` array behaves identically to today.
 */
export interface MapRoomDraft {
  id: string;
  name: string;
  kind?: MapAreaKind;
  /** Inclusive world extents: [minX, maxX]. */
  x: [number, number];
  /** Inclusive world extents: [minZ, maxZ]. */
  z: [number, number];
  floorSurface?: MapSurface;
  wallSurface?: MapSurface;
  /** Ceiling finish; omitted rooms get the floor's default ceiling. */
  ceilingSurface?: MapSurface;
  /**
   * Leave this room open to the floor above - no ceiling panel. Set on the
   * room a stair run rises through, so the flight is visible from both
   * storeys instead of disappearing into a lid.
   */
  openAbove?: boolean;
}

/**
 * A gap cut from a wall run where two rooms should connect - a doorway (or,
 * with `cased: true`, an open archway with no leaf). `axis`/`at` locate the
 * wall line the same way `MapWallSegment`'s derivation does (`axis: 'x'`
 * means the run is at constant X, running along Z); `center`/`width`
 * locate the gap along it.
 */
export interface MapOpeningDraft {
  axis: 'x' | 'z';
  at: number;
  center: number;
  width: number;
  /** No door leaf placed in the gap - an archway rather than a doorway. */
  cased?: boolean;
}

/**
 * A hole in a floor - the footprint of a stairwell or shaft. Walled like a
 * room (so it has sides and the building stays enclosed) but produces no
 * `MapFloorPanel`, so the storey below shows through it. Matches how
 * Dwindling's own house authors a stair void: a `MapStairRun` placed inside
 * it is what the player actually walks; this just says "no floor here,
 * something else is." Purely a room-tool authoring concept - a `MapStairRun`
 * placed by the "Place stairs" tool never requires one: `makeWalkable`'s
 * stair-run branch tests the run's own footprint directly and no longer
 * re-tests any wall (room-derived or hand-drawn) around it, so a flight is
 * fully walkable whether or not it sits inside an authored void.
 */
export interface MapVoidDraft {
  id: string;
  x: [number, number];
  z: [number, number];
  /** Room whose wall finish the void's own perimeter borrows. */
  finishFrom: string;
}

/**
 * @deprecated Represent interactive objects as `MapTriggerVolume` entries with
 * a registry-driven `kind` and dispatch via `triggerRegistry`. This interface
 * will be removed in a future schema version.
 */
export interface MapInteractiveObject {
  id: string;
  type: InteractiveObjectType;
  position: [number, number];
  worldY: number;
  floor: MapFloorLevel;
  radius: number;
  label: string;
}

export interface MapStairTrigger {
  id: string;
  center: [number, number];
  size: [number, number];
  fromFloor: MapFloorLevel;
  toFloor: MapFloorLevel;
  toY: number;
  spawnXZ: [number, number];
}

/**
 * A flight of stairs the player physically walks up.
 *
 * Where a {@link MapStairTrigger} teleports between floor planes, a run is a
 * sloped walkable surface: standing on it puts the player at a height
 * interpolated between the two floors, so they climb it a step at a time and
 * can stop, turn and look down halfway. Both are supported; a game may use
 * either, or both in different places.
 *
 * The run is an axis-aligned footprint. `axis` is the direction it climbs, and
 * `footAt` says which end of that axis is the bottom - so a flight can be laid
 * either way round without negative sizes.
 */
export interface MapStairRun {
  id: string;
  /** Floor the foot of the flight stands on. */
  fromFloor: MapFloorLevel;
  /** Floor the head of the flight arrives at. */
  toFloor: MapFloorLevel;
  /** The axis the flight climbs along. */
  axis: 'x' | 'z';
  /** XZ centre of the flight's footprint. */
  center: [number, number];
  /** XZ size of the footprint: the full extent in x and z. */
  size: [number, number];
  /** Which end of `axis` the foot sits at: -1 the low coordinate, +1 the high. */
  footAt: -1 | 1;
  /**
   * Tread count. A renderer uses it to draw individual treads/risers; the
   * runtime (stairRunHeight, floorStair.ts) uses it too, quantizing the
   * walkable height to each tread's flush top instead of a smooth ramp, so
   * the two agree. Omit it for a run with no stepped visual (a genuine
   * ramp/slope), where the smooth ramp is the correct walkable surface.
   */
  steps?: number;
}

export interface MapObjectTransform {
  position: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
}

export interface MapObjectInstance {
  id: string;
  assetId: string;
  floor: MapFloorLevel;
  transform: MapObjectTransform;
  params?: Record<string, unknown>;
  tags?: string[];
  /** Optional author-assigned display name (falls back to the definition name). */
  name?: string;
}

/**
 * How a `WaypointPatrol` advances once it reaches its last point: `loop`
 * wraps back to the first, `once` stops and parks there, `ping-pong` walks
 * the list forward then back, repeating indefinitely (see
 * `runtime/waypointPatrol.ts`'s `patrolSampleAt` for the exact stepping).
 */
export type PatrolLoopMode = 'loop' | 'ping-pong' | 'once';

/** One authored stop on a patrol route. */
export interface PatrolWaypoint {
  x: number;
  y: number;
  /** Omitted for a 2D patrol (treated as 0); a 3D patrol sets it. */
  z?: number;
  /** Milliseconds to wait here after arriving, before departing toward the
   * next waypoint. Defaults to 0 (pass straight through). */
  dwellMs?: number;
}

/**
 * A shared authoring primitive for straight-line NPC movement: an ordered
 * list of waypoints and a loop mode, advanced by the same per-frame clock
 * pattern sprite animation already uses (see
 * docs/game-maker-completion/design.md section 5). Used by both a 2D
 * `Tilemap2DEntity`'s `patrol` and a placed `MapCharacterInstance`'s
 * `patrol`. No pathfinding: legs between waypoints are straight lines.
 */
export interface WaypointPatrol {
  points: PatrolWaypoint[];
  loopMode: PatrolLoopMode;
  /** Travel speed between waypoints, in world units per second. */
  speed: number;
}

/**
 * A `CharacterDefinition` placed in the 3D level - the character-rig
 * equivalent of a 3D `MapObjectInstance` (which places arbitrary
 * `assetId`-registered props, not characters). `characterId` references a
 * project's `CharacterDefinition.id`; a dangling reference (the character
 * was deleted after being placed) is skipped by a renderer rather than
 * crashing the scene, the same convention `Tilemap2DEntity`'s dangling
 * `spriteId` already follows.
 */
export interface MapCharacterInstance {
  id: string;
  characterId: string;
  floor: MapFloorLevel;
  position: [number, number, number];
  /** Facing, in radians around the world Y axis. Overridden while a
   * `patrol` is moving the character (it then faces its direction of
   * travel); used as-is while idle or dwelling. */
  rotationY?: number;
  /** Optional authored patrol route (see {@link WaypointPatrol}). Omitted
   * placements stand still, exactly as before this field existed. */
  patrol?: WaypointPatrol;
}

export interface MapRoomMarker {
  id: string;
  floor: MapFloorLevel;
  position: [number, number];
  name: string;
  kind: 'spawn' | 'checkpoint' | 'event' | 'custom';
  metadata?: Record<string, unknown>;
}

/**
 * An axis-aligned rectangular volume that triggers game logic when the player
 * enters it.
 *
 * Built-in engine kinds: `'teleport'`, `'event'`, `'damage'`, `'custom'`.
 * Game-registered kinds (via `registerTriggerHandler`): `'door'`, `'radio'`,
 * `'jumpscare'` - or any string a game registers with the trigger registry.
 */
export interface MapTriggerVolume {
  id: string;
  floor: MapFloorLevel;
  center: [number, number];
  size: [number, number];
  height: number;
  /** Engine built-ins plus open-ended game-registered handler kinds. */
  kind: 'teleport' | 'event' | 'damage' | 'custom' | 'door' | 'radio' | 'jumpscare' | (string & {});
  targetFloor?: MapFloorLevel;
  targetPosition?: [number, number];
  eventName?: string;
  params?: Record<string, unknown>;
}

export interface MapLight {
  id: string;
  floor: MapFloorLevel;
  position: [number, number, number];
  kind: 'point' | 'spot' | 'ambient-override';
  color: string;
  intensity: number;
  range?: number;
  angle?: number;
  castShadow?: boolean;
  params?: Record<string, unknown>;
}

export interface MapDecal {
  id: string;
  floor: MapFloorLevel;
  position: [number, number, number];
  rotation?: [number, number, number];
  size: [number, number];
  material: string;
  opacity?: number;
  params?: Record<string, unknown>;
}

export interface MapFloorDocument {
  id: string;
  level: MapFloorLevel;
  label: string;
  panels: MapFloorPanel[];
  walls: MapWallSegment[];
  blockers: MapBlocker[];
  areas: MapAreaMeta[];
  /**
   * Rooms authored on this floor, if the room tool was used - see
   * `MapRoomDraft`. Optional and purely additive: a floor with none behaves
   * exactly as it did before this field existed, and every `walls`/
   * `panels`/`areas` entry a room ever produced still lives in those arrays
   * regardless of whether `rooms` is present.
   */
  rooms?: MapRoomDraft[];
  /** Doorways/archways cut from this floor's room-derived walls. See `MapOpeningDraft`. */
  openings?: MapOpeningDraft[];
  /** Stairwell/shaft holes in this floor's room-derived panels. See `MapVoidDraft`. */
  voids?: MapVoidDraft[];
}

export type TileCollisionKind =
  | 'none'
  | 'solid'
  | 'one-way'
  | 'hazard'
  | 'checkpoint'
  | 'crumble'
  | 'phase-barrier'
  | 'conveyor-left'
  | 'conveyor-right'
  /** Level exit: touching it ends the level (`victory` event). Solid. */
  | 'goal';

/** A paintable tile kind in a 2D tilemap: two colours, walkability, and optional styling. */
export interface Tilemap2DTile {
  id: string;
  /** Base colour. */
  a: string;
  /** Checker accent colour. */
  b: string;
  /** Blocks the player when true. Default false (walkable). */
  solid?: boolean;
  /** Specific collision classification for platformers: 'solid', 'one-way', 'hazard', 'checkpoint'. Defaults to 'solid' when solid === true, else 'none'. */
  kind?: TileCollisionKind;
  /** Optional accent/highlight colour for edges and details. */
  accent?: string;
  /** Optional glow/emission colour. When set on a tile in the main layer
   * the platformer renders a soft radial glow centred on the tile (and uses
   * it as the lantern colour of a `checkpoint` tile). */
  glow?: string;
  /** Diameter of the glow in tiles. Defaults to 3.5, or 9 for a `goal`
   * tile and 5.5 for a `checkpoint`. */
  glowSize?: number;
  /** Peak opacity of the glow, 0..1. Defaults to 0.2, or 0.35 for a `goal`
   * tile and 0.28 for a `checkpoint`. */
  glowOpacity?: number;
  /** Optional border stroke colour. */
  border?: string;
  /** Optional surface texturing pattern ('slate' | 'brick' | 'girder' | 'stone' | 'gear' | 'marble' | 'hazard' | 'conveyor' | 'checkpoint' | 'phase'). */
  pattern?: string;
  /**
   * Optional baked pixel art for the tile: a square colour grid, reusing the
   * same {@link Sprite2D} shape sprites already use. When present, a renderer
   * should paint this instead of the flat `a`/`b` checker. Absent (the
   * default) falls back to the checker, so every tile authored before this
   * field existed keeps rendering exactly as it did.
   */
  art?: Sprite2D;
}

/** The pose names the 2D platformer's physics state machine can select
 * (see `Platformer2DScene`'s animation selection). Closed, unlike a sprite's
 * own `animations` keys: a sprite may still author extra clips under any
 * name, but only these are ever chosen automatically from physics state. */
export type PoseId =
  | 'idle'
  | 'run'
  | 'skid'
  | 'jump'
  | 'fall'
  | 'wall-slide'
  | 'dash'
  | 'rewind'
  | 'shadow-form'
  | 'death-pancake'
  /** Gliding on the Moonlit Form (falls back to 'fall' when absent). */
  | 'glide'
  /** Knocked back by damage (falls back to 'fall' when absent). */
  | 'hurt'
  /** A weapon swing or shot (falls back to 'idle'/'run' when absent).
   * A sprite may also author per-weapon clips named `attack-<weaponId>`,
   * which the platformer prefers over the generic 'attack'. */
  | 'attack';

/** A named animation clip for a Sprite2D. */
export interface SpriteAnimationClip {
  /** Array of frames, each row-major size*size cells. */
  frames: (string | null)[][];
  /** Playback speed in frames per second. */
  fps?: number;
  /** Whether the animation loops continuously. Defaults to true. */
  loop?: boolean;
}

/** A pixel-art sprite authored in Pixel Studio: a square colour grid, with
 * optional flipbook frames and multi-state animation clips. `cells` is frame 0. */
export interface Sprite2D {
  id: string;
  size: number;
  /** Row-major size*size cells; null is transparent. Frame 0 of the default flipbook. */
  cells: (string | null)[];
  /** Flipbook frames after frame 0, each row-major size*size. */
  frames?: (string | null)[][];
  /** Flipbook playback rate in frames per second. Default 6. */
  fps?: number;
  /** Optional named animation clips (e.g. 'idle', 'run', 'jump', 'fall', 'wall-slide', 'dash', 'death-pancake'). */
  animations?: Record<string, SpriteAnimationClip>;
  /**
   * Where the feet are, as a fraction of the sprite's height from the top
   * (0.9375 for a 32 px sprite standing on row 29). A platformer renderer
   * aligns this row with the bottom of the collision box instead of
   * centring the sprite on it, so a tall character stands on the floor
   * rather than sinking into it. Absent keeps the sprite centred.
   */
  anchorY?: number;
}

/** Authoring category for a placed `Tilemap2DEntity`, so a level's sprites
 * can be told apart at a glance - on its own, still just a label (unlike
 * `blocking`, which drives collision): `kind: 'npc'` is what a patrol
 * authoring UI filters on, but an npc-kind entity with no `patrol` is exactly
 * as static as before this field existed. `'trigger'` predates
 * {@link Tilemap2DTriggerVolume} (the real, gameplay-wired trigger concept -
 * a rectangular volume with a handler kind, diffed against the player every
 * tick) and is kept only so an already-placed entity with this kind still
 * loads and labels correctly; Pixel Studio's Place mode no longer offers it
 * for new placements. Full 2D AI beyond straight-line patrol is still
 * roadmap. */
export type Tilemap2DEntityKind = 'decoration' | 'npc' | 'enemy' | 'item' | 'trigger';

/**
 * A sprite placed in the 2D world - the tile-grid equivalent of a 3D
 * `MapObjectInstance`.
 */
export interface Tilemap2DEntity {
  id: string;
  /** References a `Sprite2D.id` in the tilemap's `sprites` list. */
  spriteId: string;
  /** Cell position (x right, y down); fractional values are allowed for
   * off-grid placement. */
  x: number;
  y: number;
  /** Blocks player movement at its cell, like a solid tile. Off (visual
   * only, the original behaviour) by default, so every entity placed
   * before this field existed keeps working exactly as it did. */
  blocking?: boolean;
  /** See {@link Tilemap2DEntityKind}. Defaults to 'decoration'. */
  kind?: Tilemap2DEntityKind;
  /**
   * Footprint size in tiles this entity's sprite should render at, width
   * and height, anchored at (`x`, `y`) as its top-left corner. Absent (the
   * default) renders at a single tile, the fixed quad every entity has
   * always used regardless of the sprite's own pixel resolution - so every
   * entity placed before this field existed keeps rendering pixel-
   * identical to before. A sprite's own pixel size never implies a
   * footprint on its own (existing sprite categories range from 16 to 64
   * pixels square and all render at the same one-tile footprint today);
   * a multi-tile object (a building) sets this explicitly instead.
   */
  footprint?: { w: number; h: number };
  /**
   * An authored waypoint route (see {@link WaypointPatrol}): the runtime
   * walks this entity between its points in a straight line instead of
   * leaving it static. Meaningful on any entity but intended for `kind:
   * 'npc'`; absent (the default) leaves the entity exactly as static as
   * before this field existed.
   */
  patrol?: WaypointPatrol;
}

/**
 * A rectangular trigger volume in tile-space - the 2D equivalent of
 * {@link MapTriggerVolume}. `Tilemap2DEntityKind`'s `'trigger'` value is only
 * ever a label on a single-cell `Tilemap2DEntity` (no footprint, no handler
 * kind, no event/target); it cannot actually drive Logic rules or the
 * trigger registry the way a real trigger volume does. This is that real
 * volume for 2D: diffed each tick against the player's tile position (see
 * `diffTriggerContainment`), the same containment logic the 3D branch
 * already runs on `MapDocument.triggers`, producing the same
 * `trigger-enter`/`trigger-exit` Logic events and, via `dispatchTrigger`,
 * the same registry-driven handler kinds. 2D has no floor concept, so unlike
 * `MapTriggerVolume` this carries no `floor`/`height` - a caller converting
 * one to a `MapTriggerVolume` for the shared containment/dispatch code
 * fixes `floor` at 0.
 */
export interface Tilemap2DTriggerVolume {
  id: string;
  /** Tile-space centre (x right, y down), matching `Tilemap2DEntity`'s own
   * coordinate system. */
  center: [number, number];
  /** Tile-space footprint: width and height in tiles. */
  size: [number, number];
  /** See `MapTriggerVolume.kind` - the same built-in and game-registered
   * handler kinds apply here. */
  kind: MapTriggerVolume['kind'];
  targetPosition?: [number, number];
  eventName?: string;
  params?: Record<string, unknown>;
}

/**
 * The 2D layer of a map document. When present (and the project's projection
 * is 2D) the runtime renders the tilemap orthographically with top-down
 * movement and tile-driven collision instead of the 3D level.
 */
export interface Tilemap2D {
  /** Grid width in tiles. */
  width: number;
  /** Grid height in tiles. */
  height: number;
  /** Row-major width*height tile ids; null is empty (void, solid). */
  cells: (string | null)[];
  /** The tile kinds the cells reference. */
  tiles: Tilemap2DTile[];
  /** The player's sprite; a plain quad when absent. */
  playerSprite?: Sprite2D;
  /** Every sprite authored in Pixel Studio (the player's included). */
  sprites?: Sprite2D[];
  /** Spawn cell (x right, y down). Defaults to the first walkable cell. */
  spawn?: { x: number; y: number };
  /** Non-player sprites placed in the world (props, NPCs, decoration). */
  entities?: Tilemap2DEntity[];
  /** Trigger volumes placed in the world (see {@link Tilemap2DTriggerVolume}) -
   * diffed against the player's position every tick to drive Logic
   * `trigger-enter`/`trigger-exit` rules and registry-driven handlers,
   * exactly as `MapDocument.triggers` does for 3D. */
  triggers?: Tilemap2DTriggerVolume[];
  /** Movement model: 'top-down' (default) or 'platformer' (side-scroller physics). */
  mode?: 'top-down' | 'platformer';
  /** Optional background backdrop cells for structural depth (non-colliding). */
  backgroundCells?: (string | null)[];
  /** Optional foreground overlay cells (drawn in front of the player). */
  foregroundCells?: (string | null)[];
  /** Visual atmosphere / biome theme ID (e.g. 'rooftops', 'foundry', 'haunted', 'clocktower', 'moonlit'). */
  environmentTheme?: string;
}

/**
 * The current map schema version. Documents are upgraded to this on load by
 * `migrateMapDocument`, and every new document is authored at this version, so
 * deprecated fields (`interactiveObjects`) never reach persistence.
 */
export const CURRENT_MAP_VERSION = 3 as const;

export interface MapDocument {
  id: string;
  name: string;
  /**
   * Schema version. Older saved maps (1, 2) still load: `migrateMapDocument`
   * upgrades them to {@link CURRENT_MAP_VERSION}. New writes are always current.
   */
  version: 1 | 2 | 3;
  floors: MapFloorDocument[];
  objectInstances: MapObjectInstance[];
  /**
   * @deprecated Use `triggers` with registry-driven handler kinds instead.
   * Kept optional for backwards compatibility with saved maps that still carry
   * this field. Will be removed when `version` reaches 3.
   */
  interactiveObjects?: MapInteractiveObject[];
  stairTriggers: MapStairTrigger[];
  /** Flights the player physically climbs. See {@link MapStairRun}. */
  stairRuns?: MapStairRun[];
  markers?: MapRoomMarker[];
  triggers?: MapTriggerVolume[];
  lights?: MapLight[];
  decals?: MapDecal[];
  /** Placed characters (see {@link MapCharacterInstance}) - separate from
   * `objectInstances` since a character references a `CharacterDefinition`
   * by id, not an `assetId` in the generic prop registry. */
  characterInstances?: MapCharacterInstance[];
  /** Optional 2D layer (see {@link Tilemap2D}); backwards compatible. */
  tilemap2d?: Tilemap2D;
  /**
   * Which Sunder game modes this map supports. Omitted, or including
   * `'rift-tactical'`, means the full objective ruleset applies, so
   * `mapLinter`'s bomb-site check fires. A map that declares only
   * `['strike']` and/or `['surge']` skips that check: see `mapLinter.ts`'s
   * `SUNDER_NO_BOMB_SITES` rule and `@sstk/map-schema`'s `supportedModes`.
   */
  supportedModes?: GameMode[];
  settings?: {
    defaultSpawn?: {
      x: number;
      y: number;
      z: number;
      yaw?: number;
      floor?: MapFloorLevel;
    };
  };
}

export function getMapFloor(document: MapDocument, level: MapFloorLevel) {
  return document.floors.find((floor) => floor.level === level) ?? null;
}

export function getAllMapAreas(document: MapDocument) {
  return document.floors.flatMap((floor) => floor.areas);
}

/**
 * The registry-driven trigger kind each legacy interactive-object type maps to.
 * Handlers for these kinds are registered with the trigger registry by the game
 * content pack (see `registerTriggerHandler`).
 */
const INTERACTIVE_OBJECT_TRIGGER_KIND: Record<InteractiveObjectType, MapTriggerVolume['kind']> = {
  radio: 'radio',
  door: 'door',
  'jumpscare-prop': 'jumpscare',
};

/** Default interaction-volume height (metres) for a migrated interactive object. */
const MIGRATED_TRIGGER_HEIGHT = 2.5;

/**
 * Convert a deprecated interactive object into an equivalent registry-driven
 * trigger volume. The point-and-radius interaction becomes an axis-aligned box
 * centred on the object; the original label and world-Y are preserved in
 * `params` so handlers retain everything they had.
 */
function interactiveObjectToTrigger(object: MapInteractiveObject): MapTriggerVolume {
  return {
    id: object.id,
    floor: object.floor,
    center: object.position,
    size: [object.radius * 2, object.radius * 2],
    height: MIGRATED_TRIGGER_HEIGHT,
    kind: INTERACTIVE_OBJECT_TRIGGER_KIND[object.type],
    params: { label: object.label, worldY: object.worldY },
  };
}

/**
 * Upgrade a saved map document to {@link CURRENT_MAP_VERSION}. The deprecated
 * `interactiveObjects` field is retired: each entry becomes a registry-driven
 * `MapTriggerVolume` appended to `triggers`, and the field is dropped from the
 * result so it is never written back. Idempotent - a document already at the
 * current version with no interactive objects is returned unchanged in shape.
 */
export function migrateMapDocument(document: MapDocument): MapDocument {
  const { interactiveObjects, ...rest } = document;
  if (!interactiveObjects || interactiveObjects.length === 0) {
    return rest.version === CURRENT_MAP_VERSION ? rest : { ...rest, version: CURRENT_MAP_VERSION };
  }
  const migrated = interactiveObjects.map(interactiveObjectToTrigger);
  return {
    ...rest,
    version: CURRENT_MAP_VERSION,
    triggers: [...(rest.triggers ?? []), ...migrated],
  };
}

